<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules'
import SwiperCore from 'swiper'
import 'swiper/css'
import { TransformedArticle } from '~/components/cms/BlogArticlePanel/blogArticleHelper'

type CarouselProps = {
  items: TransformedArticle[]
  slidesPerView?: number
  slidesPerGroup?: number
}

const swiperBreakpoints = {
  960: { slidesPerView: 4, slidesPerGroup: 4 },
  640: { slidesPerView: 2.2, slidesPerGroup: 2 },
  0: { slidesPerView: 1.2, slidesPerGroup: 1 },
}

const props = withDefaults(defineProps<CarouselProps>(), {
  slidesPerView: 4,
  slidesPerGroup: 4,
})
const { items } = props
const swiperUtils = ref<SwiperCore | null>(null)
const isPrevDisabled = ref(true)
const isNextDisabled = ref(false)

const updateButtonState = () => {
  if (swiperUtils.value) {
    isPrevDisabled.value = swiperUtils.value.isBeginning
    isNextDisabled.value = swiperUtils.value.isEnd
  }
}

const onSwiper = (swiper: SwiperCore) => {
  swiperUtils.value = swiper
  updateButtonState()
}

const onSlideChange = () => {
  updateButtonState()
}
</script>

<template>
  <div class="swiper-container">
    <Swiper
      :modules="[Navigation, Pagination, Scrollbar, A11y]"
      @swiper="onSwiper"
      @slideChange="onSlideChange"
      :slides-per-view="slidesPerView"
      :slides-per-group="slidesPerGroup"
      :space-between="4"
      :breakpoints="slidesPerView === 4 ? swiperBreakpoints : {}"
      v-bind="$attrs"
    >
      <SwiperSlide v-for="(item, index) in items" :key="`swiper-slide-${index}`" class="swiper-slide">
        <slot name="swiperSlide" :item="item" />
      </SwiperSlide>
      <div v-show="!isPrevDisabled || !isNextDisabled" class="swiper-nav">
        <button
          @click="swiperUtils?.slidePrev()"
          :disabled="isPrevDisabled"
          :aria-label="$t('articleSwiper.button.previous')"
        >
          <svg-icon name="mhj:arrow-carousel-left" />
        </button>
        <button
          @click="swiperUtils?.slideNext()"
          :disabled="isNextDisabled"
          :aria-label="$t('articleSwiper.button.next')"
        >
          <svg-icon name="mhj:arrow-carousel-right" />
        </button>
      </div>
    </Swiper>
  </div>
</template>

<style lang="postcss">
.swiper-nav {
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 1rem;

  button {
    height: 3.5rem;
    width: 3.5rem;

    &:disabled {
      fill: var(--ui-dark-10-t40);
      color: var(--ui-dark-10-t40);
      cursor: default;
    }
  }
}
</style>
